import { logger } from '@socketsecurity/registry/lib/logger'

import { buildRootPath } from './discover-manifest-roots.mts'
import { failMsgWithBadge } from '../../utils/fail-msg-with-badge.mts'
import { serializeResultJson } from '../../utils/serialize-result-json.mts'

import type { RecursiveManifestOutcome } from './generate-recursive-manifests.mts'
import type { CResult, OutputKind } from '../../types.mts'

function renderTable(outcomes: readonly RecursiveManifestOutcome[]): string {
  // A covered reactor member is implied by its parent's line above it.
  return outcomes
    .filter(o => o.status !== 'skippedCovered')
    .map(
      o =>
        `- ${buildRootPath(o)} (${o.ecosystem}): ${o.status}${o.factsPath ? ` -> ${o.factsPath}` : ''}`,
    )
    .join('\n')
}

function summarize(outcomes: readonly RecursiveManifestOutcome[]): string {
  const generated = outcomes.filter(o => o.status === 'generated').length
  return `Generated ${generated} Socket facts file(s).`
}

export async function outputManifestDynamicSbomInference(
  result: CResult<RecursiveManifestOutcome[]>,
  outputKind: OutputKind,
): Promise<void> {
  if (!result.ok) {
    process.exitCode = result.code ?? 1
  }

  if (outputKind === 'json') {
    logger.log(serializeResultJson(result))
    return
  }

  if (!result.ok) {
    logger.fail(failMsgWithBadge(result.message, result.cause))
    const data = result.data as RecursiveManifestOutcome[] | undefined
    if (Array.isArray(data)) {
      const table = renderTable(data)
      if (table) {
        logger.log(table)
      }
      logger.log(summarize(data))
    }
    return
  }

  const table = renderTable(result.data)

  if (outputKind === 'markdown') {
    logger.log(
      [
        '# Dynamic SBOM inference',
        '',
        ...(table ? [table, ''] : []),
        summarize(result.data),
      ].join('\n'),
    )
    return
  }

  if (table) {
    logger.log(table)
  }
  logger.log(summarize(result.data))
}
